import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core'
import { MessageService } from 'primeng/api'
import { Subject, takeUntil } from 'rxjs'

import { AvanceMes, CapturaAvance, CapturaHistorial } from '../../data/interfaces/f098.interface'
import { CobranzaShellService } from '../../data/services/view/cobranza-shell.service'
import { ApiF098Service } from '../../data/services/api/api-f098.service'

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

/**
 * El Avance de Contabilidad de enero al mes elegido: de dónde sale cada mes y las capturas de los que
 * la plataforma no puede medir.
 *
 * **La plataforma gana cuando mide.** Un mes con letras en la plataforma se enseña medido y la captura
 * del libro queda al lado para comparar; solo un mes sin letras toma la cifra capturada. Por eso la
 * tabla dice, mes por mes, cuál de las dos es.
 *
 *! Capturar no toca ningún arrendamiento: son totales del mes. Corregir una cifra exige motivo y queda
 *! en el historial; dar de baja no borra —el historial es lo que explica por qué un mes cambió—.
 */
@Component({
  selector: 'app-avance-capturas',
  templateUrl: './avance-capturas.component.html',
  styleUrls: ['./avance-capturas.component.scss'],
})
export class AvanceCapturasComponent implements OnChanges, OnInit, OnDestroy {
  /** El mes elegido en el cascarón, `YYYY-MM`. */
  @Input() month = ''

  meses: AvanceMes[] = []
  capturas: CapturaAvance[] = []
  cargando = false
  error: string | null = null

  // ── Diálogo de captura ──────────────────────────────────────────────────────────────────
  mostrarCaptura = false
  capMes = ''
  capTotal: number | null = null
  capCobrado: number | null = null
  capPendiente: number | null = null
  capMotivo = ''
  capError: string | null = null
  guardando = false

  // ── Historial y baja ────────────────────────────────────────────────────────────────────
  mostrarHistorial = false
  historialDe: CapturaAvance | null = null
  mostrarBaja = false
  bajaDe: CapturaAvance | null = null
  bajaMotivo = ''
  bajaError: string | null = null
  dandoDeBaja = false

  constructor(
    private readonly api: ApiF098Service,
    private readonly toast: MessageService,
    private readonly shell: CobranzaShellService,
  ) {}

  private readonly destroy$ = new Subject<void>()

  ngOnInit(): void {
    //* Capturar desde otro lado pide refrescar el cascarón: esta tabla también.
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.month && this.cargar())
  }

  ngOnChanges(): void {
    if (this.month) this.cargar()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  cargar(): void {
    this.cargando = true
    this.error = null
    const anio = this.month.slice(0, 4)
    this.api.avanceDelAnio(this.month).subscribe((r) => {
      this.cargando = false
      if (r.error || !r.data) {
        this.error = r.msg
        return
      }
      this.meses = r.data
    })
    this.api.capturas(`${anio}-01`, `${anio}-12`).subscribe((r) => (this.capturas = r.data || []))
  }

  nombreMes(m: string): string {
    return `${MESES[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`
  }

  capturaDe(m: string): CapturaAvance | null {
    return this.capturas.find((c) => c.month === m) ?? null
  }

  fuente(m: AvanceMes): { label: string; sev: string; tip: string } {
    if (m.source === 'PLATAFORMA') return { label: 'Plataforma', sev: 'success', tip: 'Medido con las letras de la plataforma' }
    if (m.source === 'CAPTURA') return { label: 'Capturado', sev: 'warning', tip: 'La plataforma no tiene letras de este mes: se usa la cifra capturada del libro' }
    return { label: 'Sin datos', sev: 'danger', tip: 'Ni la plataforma ni una captura tienen este mes' }
  }

  /** La diferencia entre lo medido y lo capturado, para los meses en que hay las dos. */
  diferencia(m: AvanceMes): number | null {
    if (m.source !== 'PLATAFORMA' || m.captured?.total == null) return null
    return Math.round((m.total - m.captured.total) * 100) / 100
  }

  pct(r: number | null): string {
    return r == null ? 'n/a' : `${Math.round(r * 1000) / 10}%`
  }

  // ── Capturar o corregir ─────────────────────────────────────────────────────────────────

  abrirCaptura(m: AvanceMes): void {
    const c = this.capturaDe(m.month)
    this.capMes = m.month
    this.capTotal = c?.values.total ?? null
    this.capCobrado = c?.values.collected ?? null
    this.capPendiente = c?.values.pending ?? null
    this.capMotivo = ''
    this.capError = null
    this.mostrarCaptura = true
  }

  get corrigiendo(): boolean {
    return !!this.capturaDe(this.capMes)
  }

  /**
   * Lo que impide guardar, dicho antes de mandar nada. Son las MISMAS reglas que el servidor: si se
   * separan, la pantalla deja pasar algo que luego rebota con otro mensaje.
   */
  get problemaCaptura(): string | null {
    if (this.capTotal == null) return 'El total cartera del mes es obligatorio.'
    if (this.capTotal < 0 || (this.capCobrado ?? 0) < 0 || (this.capPendiente ?? 0) < 0) return 'Los montos no pueden ser negativos.'
    if (this.capCobrado != null && this.capCobrado > this.capTotal + 1) return 'Lo cobrado no puede ser mayor que el total del mes.'
    if (this.capCobrado != null && this.capPendiente != null && Math.abs(this.capTotal - this.capCobrado - this.capPendiente) > 1) {
      return `No cuadra: total ${this.pesos(this.capTotal)} ≠ cobrado ${this.pesos(this.capCobrado)} + por cobrar ${this.pesos(this.capPendiente)}.`
    }
    if (this.corrigiendo && !this.capMotivo.trim()) return 'Para corregir una cifra ya capturada escribe el motivo: queda en el historial.'
    return null
  }

  /** Completa el tercer monto con los otros dos, como hace la hoja de Contabilidad. */
  completar(): void {
    if (this.capTotal == null) return
    if (this.capCobrado != null && this.capPendiente == null) this.capPendiente = Math.round((this.capTotal - this.capCobrado) * 100) / 100
    else if (this.capPendiente != null && this.capCobrado == null) this.capCobrado = Math.round((this.capTotal - this.capPendiente) * 100) / 100
  }

  guardar(): void {
    if (this.problemaCaptura) {
      this.capError = this.problemaCaptura
      return
    }
    this.guardando = true
    this.capError = null
    this.api.guardarCaptura(this.capMes, { total: this.capTotal, collected: this.capCobrado, pending: this.capPendiente, reason: this.capMotivo.trim() || null }).subscribe((r) => {
      this.guardando = false
      //* El error se queda DENTRO del diálogo: un toast lo taparía el propio diálogo y la captura se perdería.
      if (r.error) {
        this.capError = r.msg
        return
      }
      this.mostrarCaptura = false
      this.toast.add({ severity: 'success', summary: 'Avance', detail: `${this.nombreMes(this.capMes)} ${this.corrigiendo ? 'corregido' : 'capturado'}` })
      this.cargar()
    })
  }

  // ── Historial y baja ────────────────────────────────────────────────────────────────────

  verHistorial(c: CapturaAvance): void {
    this.historialDe = c
    this.mostrarHistorial = true
  }

  autor(h: CapturaHistorial): string {
    const u = h.user
    if (!u || typeof u === 'string') return '—'
    return [u.names, u.lastNames].filter(Boolean).join(' ') || u.email || '—'
  }

  campo(f: string): string {
    return { total: 'Total cartera', collected: 'Cobrado', pending: 'Por cobrar', deletedAt: 'Baja' }[f] || f
  }

  abrirBaja(c: CapturaAvance): void {
    this.bajaDe = c
    this.bajaMotivo = ''
    this.bajaError = null
    this.mostrarBaja = true
  }

  darDeBaja(): void {
    if (!this.bajaDe) return
    if (!this.bajaMotivo.trim()) {
      this.bajaError = 'Escribe el motivo de la baja: queda en el historial.'
      return
    }
    this.dandoDeBaja = true
    this.api.quitarCaptura(this.bajaDe._id, this.bajaMotivo.trim()).subscribe((r) => {
      this.dandoDeBaja = false
      if (r.error) {
        this.bajaError = r.msg
        return
      }
      this.mostrarBaja = false
      this.toast.add({ severity: 'success', summary: 'Avance', detail: `Se dio de baja la captura de ${this.nombreMes(this.bajaDe!.month)}` })
      this.cargar()
    })
  }

  private pesos(n: number): string {
    return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
  }
}
